import React, { useMemo } from "react";

function normalize(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function toDate(value) {
  if (!value) return null;

  try {
    const parsed =
      typeof value?.toDate === "function"
        ? value.toDate()
        : new Date(value);

    return Number.isNaN(parsed.getTime())
      ? null
      : parsed;
  } catch {
    return null;
  }
}

function formatDateTime(value) {
  const parsed = toDate(value);

  return parsed
    ? parsed.toLocaleString()
    : "Date unavailable";
}

function titleCase(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function getIncidentDate(incident) {
  return (
    incident?.incidentDate ||
    incident?.occurredAt ||
    incident?.reportedAt ||
    incident?.createdAt ||
    incident?.updatedAt
  );
}

function getIncidentStatus(incident) {
  return normalize(
    incident?.status ||
      incident?.incidentStatus ||
      incident?.investigationStatus ||
      "open"
  );
}

function getSeverity(incident) {
  return normalize(
    incident?.severity ||
      incident?.priority ||
      incident?.riskLevel ||
      "standard"
  );
}

function getStatusTone(status) {
  if (
    ["resolved", "closed", "completed"].includes(
      status
    )
  ) {
    return {
      color: "#166534",
      background: "#dcfce7",
      border: "#bbf7d0",
    };
  }

  if (
    ["pending", "investigating", "under_review"].includes(
      status
    )
  ) {
    return {
      color: "#92400e",
      background: "#fef3c7",
      border: "#fde68a",
    };
  }

  return {
    color: "#b91c1c",
    background: "#fee2e2",
    border: "#fecaca",
  };
}

function getSeverityTone(severity) {
  if (
    ["critical", "high", "urgent"].includes(severity)
  ) {
    return "#b91c1c";
  }

  if (
    ["medium", "moderate"].includes(severity)
  ) {
    return "#b45309";
  }

  return "#475569";
}

export default function Shift360Incidents({
  shift,
  incidents = [],
}) {
  const shiftIncidents = useMemo(() => {
    if (!shift) return [];

    const shiftId = normalize(
      shift?.id || shift?.shiftId
    );

    return (Array.isArray(incidents)
      ? incidents
      : []
    )
      .filter((incident) => {
        const incidentShiftId = normalize(
          incident?.shiftId ||
            incident?.relatedShiftId ||
            incident?.assignedShiftId
        );

        return (
          shiftId &&
          incidentShiftId === shiftId
        );
      })
      .sort((a, b) => {
        const aDate = toDate(getIncidentDate(a));
        const bDate = toDate(getIncidentDate(b));

        return (
          (bDate?.getTime() || 0) -
          (aDate?.getTime() || 0)
        );
      });
  }, [shift, incidents]);

  const summary = useMemo(() => {
    const unresolved = shiftIncidents.filter(
      (incident) =>
        ![
          "resolved",
          "closed",
          "completed",
        ].includes(getIncidentStatus(incident))
    );

    const highRisk = shiftIncidents.filter(
      (incident) =>
        [
          "critical",
          "high",
          "urgent",
        ].includes(getSeverity(incident))
    );

    return {
      total: shiftIncidents.length,
      unresolved: unresolved.length,
      highRisk: highRisk.length,
    };
  }, [shiftIncidents]);

  if (!shift) return null;

  return (
    <div style={{ display: "grid", gap: 13 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 18,
            fontWeight: 950,
          }}
        >
          Shift Incidents
        </div>

        <div
          style={{
            marginTop: 4,
            color: "#64748b",
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          Safety, attendance, operational, and client
          incidents linked directly to this shift.
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(3, minmax(0, 1fr))",
          gap: 8,
        }}
      >
        {[
          [
            "Incidents",
            summary.total,
            "#1d4ed8",
          ],
          [
            "Unresolved",
            summary.unresolved,
            summary.unresolved > 0
              ? "#b91c1c"
              : "#166534",
          ],
          [
            "High Risk",
            summary.highRisk,
            summary.highRisk > 0
              ? "#b91c1c"
              : "#166534",
          ],
        ].map(([label, value, color]) => (
          <div
            key={label}
            style={{
              padding: 11,
              borderRadius: 13,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <div
              style={{
                color: "#64748b",
                fontSize: 8,
                fontWeight: 900,
                textTransform: "uppercase",
              }}
            >
              {label}
            </div>

            <div
              style={{
                marginTop: 5,
                color,
                fontSize: 18,
                fontWeight: 950,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      {shiftIncidents.length === 0 ? (
        <div
          style={{
            padding: 18,
            borderRadius: 13,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
            color: "#64748b",
            textAlign: "center",
            fontSize: 11,
            fontWeight: 750,
          }}
        >
          No incidents are linked to this shift.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          {shiftIncidents.map(
            (incident, index) => {
              const status =
                getIncidentStatus(incident);

              const severity =
                getSeverity(incident);

              const statusTone =
                getStatusTone(status);

              const title =
                incident?.title ||
                incident?.incidentTitle ||
                incident?.incidentType ||
                incident?.type ||
                "Shift Incident";

              const description =
                incident?.description ||
                incident?.summary ||
                incident?.details ||
                incident?.notes ||
                "No description is available.";

              const workerName =
                incident?.workerName ||
                incident?.employeeName ||
                incident?.staffName ||
                shift?.assignedWorkerName ||
                "";

              return (
                <div
                  key={
                    incident?.id ||
                    `shift-incident-${index}`
                  }
                  style={{
                    padding: 12,
                    borderRadius: 13,
                    border: `1px solid ${statusTone.border}`,
                    background: "#ffffff",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent:
                        "space-between",
                      alignItems: "flex-start",
                      gap: 9,
                    }}
                  >
                    <div>
                      <div
                        style={{
                          color: "#0f172a",
                          fontSize: 11,
                          fontWeight: 950,
                        }}
                      >
                        {titleCase(title)}
                      </div>

                      <div
                        style={{
                          marginTop: 4,
                          color: "#64748b",
                          fontSize: 9,
                          fontWeight: 750,
                        }}
                      >
                        {formatDateTime(
                          getIncidentDate(incident)
                        )}
                      </div>
                    </div>

                    <span
                      style={{
                        padding: "4px 7px",
                        borderRadius: 999,
                        color: statusTone.color,
                        background:
                          statusTone.background,
                        fontSize: 8,
                        fontWeight: 950,
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {titleCase(status)}
                    </span>
                  </div>

                  <div
                    style={{
                      marginTop: 9,
                      color: "#475569",
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1.5,
                    }}
                  >
                    {String(description)}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 6,
                      marginTop: 9,
                    }}
                  >
                    <span
                      style={{
                        padding: "4px 7px",
                        borderRadius: 999,
                        background: "#f1f5f9",
                        color:
                          getSeverityTone(severity),
                        fontSize: 8,
                        fontWeight: 900,
                      }}
                    >
                      Severity: {titleCase(severity)}
                    </span>

                    {workerName ? (
                      <span
                        style={{
                          padding: "4px 7px",
                          borderRadius: 999,
                          background: "#eff6ff",
                          color: "#1d4ed8",
                          fontSize: 8,
                          fontWeight: 900,
                        }}
                      >
                        Worker: {String(workerName)}
                      </span>
                    ) : null}
                  </div>

                  {incident?.resolution ||
                  incident?.resolutionNotes ? (
                    <div
                      style={{
                        marginTop: 9,
                        padding: 9,
                        borderRadius: 10,
                        border:
                          "1px solid #bbf7d0",
                        background: "#f0fdf4",
                        color: "#166534",
                        fontSize: 9,
                        fontWeight: 750,
                        lineHeight: 1.45,
                      }}
                    >
                      <strong>Resolution:</strong>{" "}
                      {String(
                        incident?.resolution ||
                          incident?.resolutionNotes
                      )}
                    </div>
                  ) : null}
                </div>
              );
            }
          )}
        </div>
      )}

      <div
        style={{
          color: "#64748b",
          fontSize: 9,
          fontWeight: 700,
          lineHeight: 1.45,
        }}
      >
        Only incidents whose `shiftId`,
        `relatedShiftId`, or `assignedShiftId` matches
        this shift are displayed.
      </div>
    </div>
  );
}